import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The Oswald eyebrow that titles nearly every block in the design — "Site at a
 * glance", "Onboarding · 2", "Active roster", "Off-site crew". Appears ~14×
 * with one style: `Oswald 600 .7rem/.1em uppercase #515869`.
 *
 * The roster heading pairs it with a right-aligned counter ("8 shown"), which
 * is Inter, not Oswald — `font-size:.82rem;font-weight:600;color:#515869` — and
 * the two sit on a shared baseline.
 *
 * The design's margins around this label are per-site (`14px 2px 10px` for
 * onboarding, `18px 2px 10px` for the roster, `22px 2px 10px` for off-site), so
 * spacing stays with the caller.
 */
export type SectionLabelProps = {
  children: ReactNode;
  /** Right-aligned counter or action — the roster's "{n} shown". */
  trailing?: ReactNode;
  className?: string;
};

const LABEL =
  "font-display text-[0.7rem] font-semibold uppercase tracking-[0.1em] text-muted";

/** The wizard's section eyebrows are brand orange rather than muted — see the
 *  "Required documents by role" / "Project basics" heads in the source. */
export const BRAND_LABEL =
  "font-display text-[0.7rem] font-semibold uppercase tracking-[0.1em] text-brand";

export function SectionLabel({
  children,
  trailing,
  className,
}: SectionLabelProps) {
  if (trailing === undefined) {
    return <div className={cn(LABEL, className)}>{children}</div>;
  }
  return (
    <div className={cn("flex items-baseline justify-between gap-3", className)}>
      <div className={LABEL}>{children}</div>
      <span className="text-[0.82rem] font-semibold text-muted">
        {trailing}
      </span>
    </div>
  );
}
